import React from 'react';
import {
  ArrowRight,
  BarChart3,
  Bot,
  Brain,
  ClipboardCheck,
  Code2,
  FileSearch,
  FileText,
  ListChecks,
  ShieldCheck,
  UserCheck,
  Users,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import CaseStudyProof, { caseStudyProofs } from '../../../Blog/CaseStudyProof';
import {
  CapabilityGrid,
  DEMO_PATH,
  IndustryCta,
  IndustryFlowStyles,
  IndustryWorkflow,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const SCREENING_PATH = '/hirewithus/screening-assessment';

const CANDIDATE_SIGNALS = [
  {
    title: 'Candidate score',
    helps: 'Review resume and completed assessment scores in the candidate record.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: BarChart3,
  },
  {
    title: 'Skill match',
    helps: 'Compare matching, missing, and critical skills against the role.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: ListChecks,
  },
  {
    title: 'Experience match',
    helps: 'Review years and relevant experience against the job requirements.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: FileSearch,
  },
  {
    title: 'Technical assessment',
    helps: 'Add coding and technical assessment results where the role requires them.',
    href: '/hirewithus/coding-assessment',
    icon: Code2,
  },
  {
    title: 'AI interview',
    helps: 'Bring structured AI interview responses into recruiter review.',
    href: '/hirewithus/virtualhiring',
    icon: Video,
  },
  {
    title: 'Behaviour',
    helps: 'Review behavioural assessment results alongside role-related evidence.',
    href: '/hirewithus/functional-assessment',
    icon: Brain,
  },
  {
    title: 'Communication',
    helps: 'Include communication assessment results in the candidate picture.',
    href: '/hirewithus/functional-assessment',
    icon: Users,
  },
  {
    title: 'Risk signals',
    helps: 'Review assessment integrity signals and their supporting evidence when an issue is flagged.',
    href: '/hirewithus/coding-assessment',
    icon: ShieldCheck,
  },
  {
    title: 'Evidence',
    helps: 'Give recruiters the scored results and supporting detail needed to review a candidate.',
    href: SCREENING_PATH,
    icon: FileText,
  },
];

const EVIDENCE_FLOW = [
  { title: 'Resume', detail: 'Start with role-aligned resume scoring and matching skills.', icon: FileSearch },
  { title: 'Assessments', detail: 'Add screening and technical assessment results where needed.', icon: ClipboardCheck },
  { title: 'Interviews', detail: 'Capture structured AI interview responses for review.', icon: Video },
  { title: 'Behavioural signals', detail: 'Include behavioural and communication assessment results.', icon: Brain },
  { title: 'Candidate evidence', detail: 'Bring the available signals together for recruiter review.', icon: FileText },
  { title: 'Recruiter decision', detail: 'The recruiter decides who should progress to the shortlist.', icon: UserCheck },
];

export default function AiScreening() {
  useSEO({
    title: 'AI Resume Screening & Candidate Assessment | SearchJob.ai',
    description:
      'Turn resumes, assessments, interviews and behavioural signals into a richer candidate intelligence layer for recruiter review.',
    canonical: 'https://searchjob.ai/solutions/ai-screening',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <EvidenceFlow />
        </ScrollReveal>
        <ScrollReveal>
          <CandidateIntelligence />
        </ScrollReveal>
        <ScrollReveal>
          <Proof />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-10 px-4 pb-12 pt-12 sm:px-6 lg:grid-cols-2 lg:items-stretch lg:gap-12 lg:px-8 lg:pb-14 lg:pt-16">
        <div className="flex h-full flex-col text-center lg:text-left">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Bot className="h-3.5 w-3.5" aria-hidden="true" />
            AI screening
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            Don&apos;t Screen Resumes. <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">Screen Candidates.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            Turn resumes, assessments, interviews and behavioural signals into a richer candidate intelligence layer.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={SCREENING_PATH}>
                See AI Screening
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md"
            >
              <a href={DEMO_PATH}>Book a Screening Demo</a>
            </Button>
          </div>
        </div>
        <CandidateReviewPath />
      </div>
    </section>
  );
}

function CandidateReviewPath() {
  const stages = [
    ['Candidate inputs', 'Resume, assessments, interviews, and behavioural signals'],
    ['Candidate evidence', 'Scores, matching, results, and supporting detail'],
    ['Recruiter decision', 'A person reviews the evidence and decides'],
  ];

  return (
    <aside className="relative flex h-full flex-col" aria-label="Candidate intelligence flow">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Candidate intelligence</p>
      <ol className="grid flex-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)]">
        {stages.map(([label, note], index) => (
          <React.Fragment key={label}>
            <li className="min-h-0">
              <div className="flex h-full items-start gap-3 rounded-2xl border border-primary bg-primary/5 px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-sm font-semibold text-primary">
                  {index + 1}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground">{label}</span>
                  <span className="mt-0.5 block text-sm leading-relaxed text-muted-foreground">{note}</span>
                </span>
              </div>
            </li>
            {index < stages.length - 1 ? (
              <li className="flex h-6 items-center justify-center" role="presentation">
                <span className="block h-4 w-px bg-primary/40" aria-hidden="true" />
              </li>
            ) : null}
          </React.Fragment>
        ))}
      </ol>
    </aside>
  );
}

function EvidenceFlow() {
  return (
    <Section id="candidate-evidence" muted compact>
      <SectionIntro
        eyebrow="Candidate intelligence"
        title="A resume is only one data point."
        copy="Use the signals available across the hiring process to help recruiters understand the candidate before they make a shortlist decision."
      />
      <IndustryWorkflow steps={EVIDENCE_FLOW} />
    </Section>
  );
}

function CandidateIntelligence() {
  return (
    <Section compact>
      <SectionIntro
        eyebrow="Verified signals"
        title="Bring the evidence your recruiters already use into one review path."
        copy="Each signal links to an existing SearchJob.ai capability. The recruiter remains responsible for the final decision."
      />
      <CapabilityGrid items={CANDIDATE_SIGNALS} />
    </Section>
  );
}

function Proof() {
  return (
    <Section muted compact>
      <SectionIntro
        eyebrow="Customer proof"
        title="A published technical-screening story."
        copy="manest.ai is the approved case study for applicant scoring, first-round AI interviews, coding assessments, and AI follow-up before live interviews."
      />
      <CaseStudyProof items={[caseStudyProofs.manest]} />
    </Section>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="Evidence-backed shortlist, not another pile of resumes."
      copy="Use the available candidate evidence to focus recruiter time on the people worth reviewing next."
      primaryLabel="Book a Screening Demo"
      primaryHref={DEMO_PATH}
      secondaryLabel="See AI Screening"
      secondaryHref={SCREENING_PATH}
      compact
    />
  );
}
